import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../lib/api';
import { FolderSimple, Files, CheckCircle, CircleNotch, ArrowRight, Plus, CreditCard, Lightning } from '@phosphor-icons/react';

const StatCard = ({ label, value, icon: Icon, color }) => (
  <div className="bg-white border border-black/15 p-6" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <div className="flex items-start justify-between">
      <div>
        <p className="font-mono text-xs uppercase tracking-wider text-[#8F8F8F]">{label}</p>
        <p className="font-sans text-3xl font-black mt-2">{value}</p>
      </div>
      <div className={`w-10 h-10 flex items-center justify-center ${color}`}>
        <Icon size={20} weight="bold" className="text-white" />
      </div>
    </div>
  </div>
);

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, subRes] = await Promise.all([
          api.get('/dashboard/stats'),
          api.get('/subscription/me'),
        ]);
        setStats(statsRes.data);
        setSubscription(subRes.data);
      } catch (err) {
        console.error('Failed to fetch data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-black/5 w-48" />
          <div className="grid grid-cols-1 md:grid-cols-4 gap-0 border border-black/15">
            {[1, 2, 3, 4].map(i => <div key={i} className="h-28 bg-white border-r border-black/15" />)}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8" data-testid="dashboard-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <p className="font-mono text-xs font-bold tracking-[0.2em] uppercase text-[#FF5A00]">Overview</p>
          <h1 className="font-sans text-3xl md:text-4xl font-black tracking-tight mt-1">Dashboard</h1>
        </div>
        <Link
          to="/projects"
          data-testid="dashboard-new-project-btn"
          className="inline-flex items-center gap-2 bg-[#FF5A00] text-white px-6 py-3 font-mono text-sm uppercase tracking-wide hover:bg-[#E04E00] transition-all duration-150 hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none shadow-[4px_4px_0px_rgba(0,0,0,1)]"
        >
          <Plus size={16} weight="bold" /> New Project
        </Link>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-0 border border-black/15 mb-8">
        <div className="border-r border-black/15">
          <StatCard label="Projects" value={stats?.total_projects || 0} icon={FolderSimple} color="bg-black" />
        </div>
        <div className="border-r border-black/15">
          <StatCard label="Files Uploaded" value={stats?.total_files || 0} icon={Files} color="bg-[#0047FF]" />
        </div>
        <div className="border-r border-black/15">
          <StatCard label="Analyzed" value={stats?.analyzed_files || 0} icon={CheckCircle} color="bg-[#00A859]" />
        </div>
        <div>
          <StatCard label="BOQ Results" value={stats?.total_boq_results || 0} icon={CircleNotch} color="bg-[#FF5A00]" />
        </div>
      </div>

      {/* Subscription & Usage */}
      {subscription && (
        <div className="bg-white border border-black/15 mb-8" data-testid="subscription-card">
          <div className="p-6 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-[#FF5A00] flex items-center justify-center">
                <CreditCard size={24} weight="bold" className="text-white" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <p className="font-sans text-lg font-bold tracking-tight">{subscription.plan_name || 'Free'} Plan</p>
                  <span className={`px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider ${
                    subscription.plan_id === 'free' ? 'bg-black/5 text-[#8F8F8F]' : 'bg-[#00A859]/10 text-[#00A859] border border-[#00A859]/30'
                  }`}>
                    {subscription.status || 'active'}
                  </span>
                </div>
                <p className="font-mono text-xs text-[#5E5E5E] mt-0.5">
                  {subscription.plan_id === 'free' ? 'Upgrade to unlock more analyses and export features' : `${subscription.plan_price} SAR/month`}
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="font-mono text-xs text-[#8F8F8F] uppercase tracking-wider">Usage this month</p>
              <p className="font-sans text-2xl font-black mt-1">
                {subscription.analyses_used || 0}<span className="text-[#8F8F8F] text-sm font-mono">/{subscription.analyses_limit === 999999 ? '∞' : subscription.analyses_limit}</span>
              </p>
              <div className="w-32 h-1.5 bg-black/10 mt-2">
                <div
                  className="h-full bg-[#FF5A00] transition-all"
                  style={{ width: `${Math.min(100, ((subscription.analyses_used || 0) / (subscription.analyses_limit || 1)) * 100)}%` }}
                />
              </div>
            </div>
          </div>
          {subscription.plan_id === 'free' && (
            <div className="px-6 py-3 bg-[#F7F7F7] border-t border-black/10 flex items-center justify-between">
              <p className="font-mono text-xs text-[#5E5E5E]">
                <Lightning size={12} weight="bold" className="inline text-[#FF5A00]" /> {3 - (subscription.analyses_used || 0)} free analyses remaining
              </p>
              <Link to="/#pricing" data-testid="upgrade-plan-btn" className="font-mono text-xs uppercase tracking-wide text-[#FF5A00] hover:underline font-bold">
                Upgrade Plan
              </Link>
            </div>
          )}
        </div>
      )}

      {/* Recent Projects */}
      <div className="bg-white border border-black/15">
        <div className="p-6 border-b border-black/15 flex items-center justify-between">
          <h2 className="font-sans text-xl font-bold tracking-tight">Recent Projects</h2>
          <Link to="/projects" data-testid="view-all-projects-link" className="font-mono text-xs uppercase tracking-wide text-[#FF5A00] hover:underline flex items-center gap-1">
            View All <ArrowRight size={12} weight="bold" />
          </Link>
        </div>

        {stats?.recent_projects?.length > 0 ? (
          <div className="divide-y divide-black/10">
            {stats.recent_projects.map((project) => (
              <Link
                key={project.project_id}
                to={`/projects/${project.project_id}`}
                data-testid={`project-row-${project.project_id}`}
                className="flex items-center justify-between p-4 hover:bg-[#F7F7F7] transition-colors"
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-[#F7F7F7] border border-black/15 flex items-center justify-center">
                    <FolderSimple size={20} weight="regular" className="text-[#5E5E5E]" />
                  </div>
                  <div>
                    <p className="font-sans text-sm font-bold">{project.name}</p>
                    <p className="font-mono text-xs text-[#8F8F8F]">{project.description || 'No description'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-6">
                  <span className="font-mono text-xs text-[#5E5E5E]">{project.file_count || 0} files</span>
                  <span className={`px-3 py-1 font-mono text-[10px] uppercase tracking-wider ${
                    project.boq_status === 'generated' ? 'bg-[#00A859]/10 text-[#00A859] border border-[#00A859]/30' :
                    project.boq_status === 'processing' ? 'bg-[#FFC107]/10 text-[#FFC107] border border-[#FFC107]/30' :
                    'bg-black/5 text-[#8F8F8F] border border-black/10'
                  }`}>
                    {project.boq_status}
                  </span>
                  <ArrowRight size={16} weight="regular" className="text-[#8F8F8F]" />
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="p-12 text-center">
            <FolderSimple size={48} weight="regular" className="text-[#8F8F8F] mx-auto mb-4" />
            <p className="font-sans text-lg font-bold mb-2">No Projects Yet</p>
            <p className="font-mono text-sm text-[#5E5E5E] mb-6">Create your first project to start analyzing drawings</p>
            <Link
              to="/projects"
              data-testid="create-first-project-btn"
              className="inline-flex items-center gap-2 bg-black text-white px-6 py-3 font-mono text-sm uppercase tracking-wide hover:bg-black/80 transition-colors"
            >
              <Plus size={16} weight="bold" /> Create Project
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
